/* eslint-disable @typescript-eslint/no-explicit-any */
import MapboxViewer from "@/components/mapbox/MapboxViewer";
import BaseController from "@/components/mapbox/Lib/Controllers/BaseController";
import { autobind } from "core-decorators";
import { MapboxMeasureAreaObject } from "@/components/mapbox/Lib/Objects/MapboxMeasureAreaObject";
import type { VertexInfo } from "../Utils/ObjectUtils";
import type { UpdateMeasureFeatureDto } from "@/types/dto";
import { calculateArea } from "@/utils/geometry-utils";

class EditorMeasureAreaController extends BaseController {
  public static name = "EditorMeasureAreaController";
  public static event = {
    COMPLETE: "COMPLETE",
  };

  private _selectedFeature: MapboxMeasureAreaObject | null = null;
  private _selectedVertex: VertexInfo | null = null;
  private _isEditing: boolean = false;
  private _isDraggingMeasure: boolean = false;
  private _dragStartPosition: { lng: number; lat: number } | null = null;
  private _originalCoordinates: number[][] | null = null;

  constructor(mapboxViewer: MapboxViewer) {
    super(mapboxViewer);
    this._mapboxViewer = mapboxViewer;
    this._canvas.addEventListener("pointerdown", this.onPointerDownController);
    this._canvas.addEventListener("pointermove", this.onPointerMoveController);
  }

  get selectedFeature() {
    return this._selectedFeature;
  }

  public set enabled(value: boolean) {
    this._enabled = value;
  }

  public get enabled() {
    return this._enabled;
  }

  @autobind
  attached(object: MapboxMeasureAreaObject) {
    if (!this._enabled) {
      return;
    }
    if (object.content.config?.isLocked) {
      return;
    }
    this._selectedFeature = object;
    // Enable vertex editing mode
    this._selectedFeature.setEditing(true);
  }

  @autobind
  onPointerDownController(event: PointerEvent) {
    if (!this._enabled) return;
    if (!this._selectedFeature) return;

    const canvasRect = this._canvas.getBoundingClientRect();
    this._onDownPosition.x = event.clientX - canvasRect.left;
    this._onDownPosition.y = event.clientY - canvasRect.top;

    // Store drag start position in geographic coordinates
    const dragStartLngLat = this._mapboxViewer._mapViewer.unproject([
      this._onDownPosition.x,
      this._onDownPosition.y,
    ]);
    this._dragStartPosition = {
      lng: dragStartLngLat.lng,
      lat: dragStartLngLat.lat,
    };

    // Query for vertex features first (highest priority)
    const vertexFeatures = this._mapboxViewer._mapViewer.queryRenderedFeatures(
      this._onDownPosition,
      {
        layers: ["measure-area-vertices", "measure-area-midpoints"],
        filter: [
          "all",
          ["==", ["get", "app"], "logistic-app"],
          ["==", ["get", "measureId"], this._selectedFeature.contentId],
        ],
      }
    );

    if (vertexFeatures.length > 0) {
      // Vertex editing mode
      const vertexFeature = vertexFeatures[0];
      this._selectedVertex = {
        type: vertexFeature.properties?.vertexType as "corner" | "midpoint",
        index: vertexFeature.properties?.vertexIndex as number,
        insertIndex: vertexFeature.properties?.insertIndex as number,
        featureId: vertexFeature.id as string,
      };

      this._mapboxViewer.toggleCamera(false);
      this._isEditing = true;
      this._isDraggingMeasure = false;
      this._canvas.addEventListener("pointerup", this.onPointerUpController);
      return;
    }

    // Query for measure body
    const measureFeatures = this._mapboxViewer._mapViewer.queryRenderedFeatures(
      this._onDownPosition,
      {
        layers: ["measure-area-fill", "measure-area-outline"],
        filter: [
          "all",
          ["==", ["get", "app"], "logistic-app"],
          ["==", ["get", "contentId"], this._selectedFeature.contentId],
        ],
      }
    );

    if (measureFeatures.length > 0) {
      // Measure dragging mode
      this._isDraggingMeasure = true;
      this._isEditing = true;
      this._selectedVertex = null;

      // Store original coordinates for reference
      this._originalCoordinates = this._selectedFeature.content.coordinates.map(
        (coord) => [...coord]
      );

      this._mapboxViewer.toggleCamera(false);
      this._canvas.addEventListener("pointerup", this.onPointerUpController);

      // Show visual feedback
      this._selectedFeature.setDragHighlight(true);

      console.log("Started measure drag mode");
    } else {
      console.log("No measure features found at click position");
    }
  }

  @autobind
  onPointerMoveController(event: PointerEvent) {
    if (!this._enabled) return;
    if (!this._isEditing) return;
    if (!this._selectedFeature) return;
    if (!this._dragStartPosition) return;

    this._isDragging = true;

    const canvasRect = this._canvas.getBoundingClientRect();
    const point = {
      x: event.clientX - canvasRect.left,
      y: event.clientY - canvasRect.top,
    };

    const currentLngLat = this._mapboxViewer._mapViewer.unproject([
      point.x,
      point.y,
    ]);

    if (this._isDraggingMeasure && this._originalCoordinates) {
      // Calculate offset from drag start position
      const deltaLng = currentLngLat.lng - this._dragStartPosition.lng;
      const deltaLat = currentLngLat.lat - this._dragStartPosition.lat;

      // Move entire measure by applying offset to all coordinates
      const newCoordinates = this._originalCoordinates.map((coord) => [
        coord[0] + deltaLng,
        coord[1] + deltaLat,
      ]);

      // Update the measure coordinates
      this._selectedFeature.moveMeasure(newCoordinates);
    } else if (this._selectedVertex) {
      // Vertex editing mode
      if (
        this._selectedVertex.type === "corner" &&
        this._selectedVertex.index !== undefined
      ) {
        this._selectedFeature.updateVertex(this._selectedVertex.index, [
          currentLngLat.lng,
          currentLngLat.lat,
        ]);
      } else if (this._selectedVertex.type === "midpoint") {
        // For midpoints, don't do anything during move - we'll handle it on pointer up
      }
    }
  }

  @autobind
  onPointerUpController(event: PointerEvent) {
    if (!this._enabled || !this._selectedFeature) {
      this.cleanup();
      return;
    }

    this._mapboxViewer.toggleCamera(true);

    const canvasRect = this._canvas.getBoundingClientRect();
    const point = {
      x: event.clientX - canvasRect.left,
      y: event.clientY - canvasRect.top,
    };

    if (this._isDraggingMeasure && this._isDragging) {
      // Finalize measure move
      const finalLngLat = this._mapboxViewer._mapViewer.unproject([
        point.x,
        point.y,
      ]);

      if (this._dragStartPosition && this._originalCoordinates) {
        const deltaLng = finalLngLat.lng - this._dragStartPosition.lng;
        const deltaLat = finalLngLat.lat - this._dragStartPosition.lat;

        const finalCoordinates = this._originalCoordinates.map((coord) => [
          coord[0] + deltaLng,
          coord[1] + deltaLat,
        ]);

        // Update content with final coordinates
        this._selectedFeature.updateContent({
          ...this._selectedFeature.content,
          coordinates: finalCoordinates,
          properties: {
            ...this._selectedFeature.content.properties,
          },
        });

        this.complete();
      }
    } else if (
      this._selectedVertex &&
      this._selectedVertex.type === "midpoint"
    ) {
      // Handle midpoint click - insert new vertex
      const lngLat = this._mapboxViewer._mapViewer.unproject([
        point.x,
        point.y,
      ]);

      if (this._selectedVertex.insertIndex !== undefined) {
        console.log(
          "Inserting vertex at index:",
          this._selectedVertex.insertIndex
        );
        this._selectedFeature.insertVertex(this._selectedVertex.insertIndex, [
          lngLat.lng,
          lngLat.lat,
        ]);
        this.complete();
      }
    } else if (
      this._selectedVertex &&
      this._selectedVertex.type === "corner" &&
      this._isDragging
    ) {
      // Handle corner vertex dragging completion
      this.complete();
    }

    this.cleanup();
  }

  @autobind
  handleKeyDown(event: KeyboardEvent) {
    if (!this._selectedFeature || !this._selectedVertex) return;

    if (event.key === "Delete" || event.key === "Backspace") {
      if (
        this._selectedVertex.type === "corner" &&
        this._selectedVertex.index !== undefined
      ) {
        this._selectedFeature.removeVertex(this._selectedVertex.index);
        this._selectedVertex = null;
      }
    }
  }

  @autobind
  handleClick(event: PointerEvent) {
    this._mapboxViewer.toggleCamera(true);
    if (!this._enabled) return;
    if (!this._selectedFeature) return;
    if (event.button !== 0) return;

    // Only consider a click if no dragging occurred
    if (!this._onDownPosition.equals(this._onUpPosition)) {
      if (this._isEditing && this._isDragging) {
        this.complete();
      }
    }
    this.cleanup();
  }

  @autobind
  complete() {
    if (!this._selectedFeature) return;

    const area = calculateArea(
      this._selectedFeature.content.coordinates as [number, number][]
    );
    const updatedContent: UpdateMeasureFeatureDto = {
      ...this._selectedFeature.content,
      coordinates: this._selectedFeature.content.coordinates,
      properties: {
        ...this._selectedFeature.content.properties,
        area: area,
        unit: "m²",
        measureType: "area",
      },
    };

    // Dispatch completion event
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    //@ts-ignore
    this.dispatchEvent({
      type: EditorMeasureAreaController.event.COMPLETE,
      content: updatedContent,
    });

    this.detached();
    this._mapboxViewer.controllers.interactionController?.editorDetach();
  }

  @autobind
  detached() {
    if (this._selectedFeature) {
      this._selectedFeature.setEditing(false);
      this._selectedFeature.setDragHighlight(false);
    }
    this.cleanup();
    this._selectedFeature = null;
  }

  @autobind
  private cleanup() {
    this._isEditing = false;
    this._isDragging = false;
    this._isDraggingMeasure = false;
    this._selectedVertex = null;
    this._dragStartPosition = null;
    this._originalCoordinates = null;
    this._canvas.removeEventListener("pointerup", this.onPointerUpController);

    if (this._selectedFeature) {
      this._selectedFeature.setDragHighlight(false);
    }
  }

  @autobind
  dispose() {
    this._canvas.removeEventListener(
      "pointerdown",
      this.onPointerDownController
    );
    this._canvas.removeEventListener(
      "pointermove",
      this.onPointerMoveController
    );
    this._canvas.removeEventListener("pointerup", this.onPointerUpController);
    this.cleanup();
  }
}

export default EditorMeasureAreaController;
